/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 *
 *  @author estelle.giuly
 *
 */
export interface BpmnWorkflow {
  key: string;
  name: string;
  category: string;
}
export interface MinimalEmployee {
  employeeNumber: string;
  fullName: string;
}

export interface ProcessDefinition {
  id: string;
  url: string;
  key: string;
  name: string;
  description: string;
  category: string;
  version: number;
  resource: string;
  diagramResource: string;
  suspended: boolean;
  deploymentId: string;
  deploymentUrl: string;
  startFormDefined: boolean;
  tenantId: string;
}

export interface ProcessDefinitions {
  data: ProcessDefinition[];
  total: number;
  start: number;
  sort: string;
  order: 'asc' | 'desc';
  size: number;
}

export interface ProcessInstance {
  id: string;
  name: string;
  businessKey: string;
  suspended: boolean;
  ended?: boolean;
  processDefinitionId: string;
  processDefinitionName: string;
  processDefinitionDescription: string;
  activityId: string;
  startUserId: string;
  startTime: string;
  variables: Variable[];
  tenantId: string;
  completed: boolean;
  cancellable?: boolean;
  translationKeys?: TranslationKeys;
}

export interface HistoricProcessInstance {
  id: string;
  name: string;
  businessKey: string;
  suspended?: boolean;
  ended?: boolean;
  processDefinitionId: string;
  processDefinitionName: string;
  processDefinitionDescription: string;
  startTime: string;
  endTime: string;
  durationInMillis: number;
  startUserId: string;
  startActivityId: string;
  endActivityId: string;
  deleteReason: string;
  superProcessInstanceId: string;
  variables: Variable[];
  tenantId: string;
  translationKeys?: TranslationKeys;
}

export interface Task {
  id: string;
  owner: string;
  details: string;
  candidates: MinimalEmployee[];
  assignee?: MinimalEmployee;
  delegationState: string;
  name: string;
  description: string;
  type: string;
  requester: MinimalEmployee;
  subject: MinimalEmployee;
  status: RequestStatus;
  startTime: string;
  endTime?: string;
  dueDate: string;
  suspended: boolean;
  priority: number;
  claimTime?: string;
  taskDefinitionKey: string;
  scopeDefinitionId?: string;
  tenantId: string;
  category?: string;
  isFallbackRecipient?: boolean;
  formKey: string;
  parentTaskId: string;
  executionId: string;
  processInstanceId: string;
  processDefinitionId: string;
  variables: Variable[];
  translationKeys?: TranslationKeys;
  read?: boolean;
}

export interface HistoricTask {
  id: string;
  owner: string;
  details: string;
  type: string;
  assignee: MinimalEmployee;
  requester: MinimalEmployee;
  subject: MinimalEmployee;
  status: RequestStatus;
  name: string;
  description: string;
  startTime: string;
  endTime: string;
  deleteReason: string;
  durationInMillis: number;
  workTimeInMillis: number;
  dueDate: string;
  suspended?: boolean;
  priority: number;
  claimTime?: string;
  taskDefinitionKey: string;
  tenantId: string;
  category?: string;
  formKey: string;
  parentTaskId?: string;
  parentTaskUrl?: string;
  executionId: string;
  processInstanceId: string;
  processDefinitionId: string;
  variables: Variable[];
  translationKeys?: TranslationKeys;
}

export interface TasksResponse {
  data: Task[];
  total: number;
}

export interface HistoricTasksResponse {
  data: HistoricTask[];
  total: number;
}

export interface ProcessInstancesResponse {
  data: Request[];
  total?: number;
}

export interface HistoricProcessInstanceResponse {
  data: HistoricProcessInstance[];
  total: number;
}

export interface TranslationKeys {
  title: string;
  subtitle: string;
  description?: string;
}

export interface Variable {
  name: string;
  value: any;
  type?: string;
  scope?: 'local' | 'global';
}

export interface FormDefinition {
  id: string;
  category: string;
  name: string;
  key: string;
  description: string;
  version: number;
  resourceName: string;
  deploymentId: string;
  tenantId: string;
}

export interface FormDefinitions {
  data: FormDefinition[];
  total: number;
  start: number;
  sort: string;
  order: 'asc' | 'desc';
  size: number;
}

export interface FormModel {
  id: string;
  name: string;
  key: string;
  version: number;
  fields: Array<FormField>;
  outcomes: Array<FormOutcome>;
}

export interface FormField {
  fieldType: 'FormField' | 'OptionFormField';
  id: string;
  name: string;
  type: string;
  value: string;
  required: boolean;
  readOnly: boolean;
  overrideId: boolean;
  placeholder: string;
  layout: string;
  options?: Array<{
    id: string,
    name: string
  }>;
  params?: any;
  expression?: string;
  taskVariables?: Variable[];
}

export interface FormOutcome {
  id: string;
  name: string;
  visible?: boolean;
  enabled?: boolean;
  updatedRequest?: Variable;
}

export interface Request {
  id: string;
  instanceShortId?: string;
  url: string;
  name: string;
  type: string;
  descriptionTranslationKey: string;
  businessKey: string;
  startTime: string;
  endTime: string;
  details: string;
  tenantId: string;
  startUserId: string;
  processDefinitionId: string;
  processDefinitionUrl: string;
  processDefinitionName: string;
  processDefinitionDescription: string;
  variables: Variable[];
  definitionKey: string;
  translationKeys?: TranslationKeys;
  requester: Requester;
  subject: Requester;
  assignee: Requester;
  candidates: Requester[];
  status: RequestStatus;
  statusDetails: string;
  validationType?: ValidationType;
  steps?: RequestStep[];
  cancellable?: boolean;
  isReassignable?: boolean;
}

export interface Requester {
  employeeNumber: string;
  fullName: string;
}

export enum RequestStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  VALIDATED = 'VALIDATED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  DELETED = 'DELETED',
  ERROR = 'ERROR'
}

export enum RequestStepStatus {
  STARTED = 'STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  VALIDATED = 'VALIDATED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  ERROR = 'ERROR',
  ENDED = 'ENDED'
}

export enum ValidationType {
  NO_VALIDATION = 'NO_VALIDATION',
  ONE_STEP_VALIDATION = 'ONE_STEP_VALIDATION',
  TWO_STEP_VALIDATION = 'TWO_STEP_VALIDATION',
  MULTI_STEP_VALIDATION = 'MULTI_STEP_VALIDATION'
}
export interface RequestStep {
  assignee: Requester;
  candidates: Requester[];
  createTime: string;
  endTime: string;
  comment?: string;
  status: RequestStepStatus;
  type: 'START_STEP' | 'END_STEP' | 'TASK_STEP';
  validationInfo: { treeName: string, level: number };
}

export enum TaskDescriptionType {
  GP_EMPLOYEE_FORM_DESCRIPTOR_TASK = 'GP_EMPLOYEE_FORM_DESCRIPTOR_TASK',
  CLAIM_TASK = 'CLAIM_TASK',
  DEFAULT_TASK = 'DEFAULT_TASK'
}

export enum FieldType {
  COMMENT = 'comment',
  TASK_DESCRIPTION = 'taskDescription'
}

